import { unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { toBody, type WebappScope, type WebappSettings, type WebappStudioOut } from "./webapp-model";

const path = (agentId: string) => ({ params: { path: { agent_id: agentId } } });

export const webappKeys = {
  design: (wsId: string, agentId: string) => ["ws", wsId, "webapp-studio", agentId] as const,
};

export function useWebappDesign(agentId: string): UseQueryResult<WebappStudioOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: webappKeys.design(wsId, agentId), queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/webapp", path(agentId))) });
}

function useDesignCache(agentId: string) {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return (design: WebappStudioOut) => {
    queryClient.setQueryData(webappKeys.design(wsId, agentId), design);
  };
}

/** Save the design the form shows: the assistant's own, or the workspace default. Live for visitors at once. */
export function useSaveWebappDesign(agentId: string) {
  const api = useApi();
  const store = useDesignCache(agentId);
  return useMutation({
    mutationFn: (input: { scope: WebappScope; settings: WebappSettings }) =>
      unwrap(api.PUT("/v1/agents/{agent_id}/webapp", { ...path(agentId), body: toBody(input.scope, input.settings) })),
    onSuccess: store,
  });
}

/** Drop the assistant's own design (it follows the workspace default again) or the workspace default itself. */
export function useResetWebappDesign(agentId: string) {
  const api = useApi();
  const store = useDesignCache(agentId);
  return useMutation({
    mutationFn: (scope: WebappScope) => unwrap(api.DELETE("/v1/agents/{agent_id}/webapp", { params: { path: { agent_id: agentId }, query: { scope } } })),
    onSuccess: store,
  });
}
